/**
 * VENDORED from `@crehana/compass.web/src/page-layout/page-layout.tsx`
 * (CORE-1671). The published `@crehana/compass.web/page-layout` is
 * modern.js-coupled — its `PageLayoutBreadcrumb` calls `useNavigate` from
 * `the modern.js runtime router`, which does not exist in this app and
 * crashes the page at runtime (`useNavigate is not a function`).
 *
 * This copy replaces the ONLY two modern.js couplings:
 *  - `the modern.js runtime head`   → `next/head` (this is a Next.js app)
 *  - `the modern.js runtime router` → a `react-router-dom` v5 shim, since
 *    this app's `/learn` SPA is routed with react-router-dom v5, NOT
 *    `next/router`.
 * Everything else is byte-for-byte the upstream source (framework-agnostic).
 */
import { forwardRef, type HTMLAttributes } from 'react';

import { Breadcrumb } from '@crehana/compass.ui/breadcrumb';
import { Header, HeaderTitle } from '@crehana/compass.ui/header';
import { clsx } from 'clsx';
import Head from 'next/head';
// biome-ignore lint/style/noRestrictedImports: react-router-dom v5 is the /learn SPA router; the vendored layout shims modern.js useNavigate onto it
import { useHistory } from 'react-router-dom';

// react-router-dom v5 shim for modern.js `useNavigate` — returns an
// imperative navigate fn. This app has no `next/router` navigation for
// `/learn`; it uses react-router-dom v5's `useHistory`.
const useNavigate = () => {
  const history = useHistory();

  return (href: string) => {
    history.push(href);
  };
};

export const PageLayoutContainer = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement> & { fullScreen?: boolean }
>(({ className, fullScreen, ...props }, ref) => (
  <div
    ref={ref}
    className={clsx(
      'tw-block tw-mx-auto tw-w-full tw-px-2 sm:tw-px-2.4 md:tw-px-3.2',
      fullScreen
        ? 'tw-max-w-[1152px] lg:tw-px-0'
        : 'tw-max-w-[1462px] lg:tw-px-4.8',
      className,
    )}
    {...props}
  />
));

PageLayoutContainer.displayName = 'Container';

type TPageLayoutProps = {
  metadata?: {
    title?: string;
    description?: string;
  };
  /** @deprecated amplitude is no longer used */
  tracking?: {
    onFirstRenderEvent?: unknown;
  };
};

export const PageLayout: React.FC<
  React.PropsWithChildren<TPageLayoutProps>
> = ({ children, metadata }) => {
  return (
    <>
      {metadata && (
        <Head>
          {metadata.title && <title>{`${metadata.title} | Crehana`}</title>}
          {metadata.description && (
            <meta name="description" content={metadata.description} />
          )}
        </Head>
      )}
      {children}
    </>
  );
};

export const PageLayoutTop: React.FC<
  React.PropsWithChildren<React.ComponentProps<typeof PageLayoutContainer>> & {
    /**
     * Breadcrumb-only pages (no title/subtitle/cta filling this area) look
     * wrong with the default header background — it renders as a stray
     * white/grey box behind just the breadcrumb. Pass `transparent` on
     * those usages so the breadcrumb sits directly on the page background,
     * matching pages that don't use `PageLayoutTop` at all. Pages that
     * render a full header (title + subtitle, e.g. `DashboardPageLayout`)
     * should keep the default background.
     */
    transparent?: boolean;
  }
> = ({ className, transparent, ...props }) => {
  return (
    <div
      className={clsx('tw-pt-3.2', !transparent && 'tw-bg-neutral-light-100')}
    >
      <PageLayoutContainer
        className={clsx('tw-space-y-2.4', className)}
        {...props}
      />
    </div>
  );
};

export type TPageLayoutBreadcrumbItem =
  | {
      text: string;
    }
  | {
      text: string;
      href: string;
      navigationType: 'client-side' | 'external';
    };

type TPageLayoutBreadcrumbProps = {
  items?: TPageLayoutBreadcrumbItem[];
  isLoading?: boolean;
};

export const PageLayoutBreadcrumb = ({
  items,
  isLoading = false,
  className,
}: TPageLayoutBreadcrumbProps & { className?: string }) => {
  const navigate = useNavigate();

  if (isLoading) {
    const itemsCount = items?.length || 3;
    const skeletonItems = Array.from({ length: itemsCount }, (_, index) => (
      <div
        key={`skeleton-breadcrumb-${
          // biome-ignore lint/suspicious/noArrayIndexKey: index as key is acceptable here
          index
        }`}
        className="tw-flex tw-items-center"
      >
        {index > 0 && (
          <span className="tw-text-neutral-light-500 tw-mx-0.8">/</span>
        )}
        <div className="tw-h-1.6 tw-w-12 tw-bg-neutral-light-400 tw-rounded tw-animate-pulse" />
      </div>
    ));

    return (
      <div className={clsx('tw-flex tw-items-center', className)}>
        {skeletonItems}
      </div>
    );
  }

  if (!items) {
    return null;
  }

  const adaptedItems = items.map(item => {
    if ('href' in item) {
      if (item.navigationType === 'external') {
        return {
          text: item.text,
          href: item.href,
        };
      }
      return {
        text: item.text,
        onNavigate: () => navigate(item.href),
      };
    }

    return item;
  });

  return (
    <div className={clsx(className)}>
      <Breadcrumb items={adaptedItems} />
    </div>
  );
};

export const PageLayoutHeader = Header;

export const PageLayoutTitle = HeaderTitle;

// Provisory: Need to update the compass.ui component because it was a <p> tag and we need to use a <div>
export const PageLayoutSubtitle: React.FC<
  React.PropsWithChildren<React.HTMLAttributes<HTMLDivElement>>
> = ({ className, ...props }) => {
  return (
    <div
      className={clsx(
        'tw-font-body4 tw-text-neutral-light-700 dark:tw-text-neutral-dark-600',
        className,
      )}
      {...props}
    />
  );
};

export const PageLayoutContent: React.FC<
  React.PropsWithChildren<React.ComponentProps<typeof PageLayoutContainer>>
> = ({ className, ...props }) => {
  return (
    <PageLayoutContainer
      className={clsx('tw-mt-2.4 tw-mb-4', className)}
      {...props}
    />
  );
};

export const DashboardPageSectionContainer = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  return (
    <div className="tw-rounded-1 tw-bg-neutral-light-100 tw-elevate-01">
      {children}
    </div>
  );
};
